import { memo, useCallback, useMemo, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Clip, type ScopedPermissions, type ShortUrlResponse } from '@air/api/types';
import { isPdfRenderedExtension } from '@air/utils-clips';

import { EditableClipSharePrivateLink } from '~/components/SharePrivateClipModal/EditableClipSharePrivateLink';
import { useUpdateShareClipLink } from '~/components/SharePrivateClipModal/hooks/useUpdateShareClipLink';
import { SharePrivateClipPDFPage } from '~/components/SharePrivateClipModal/SharePrivateClipPDFPage';
import {
  EnableSharedLinkToggle,
  type EnableSharedLinkToggleProps,
} from '~/components/SharePrivateContentModal/EnableSharedLinkToggle';
import { EnableShareLinkDownloadToggle } from '~/components/SharePrivateContentModal/EnableShareLinkDownloadToggle';
import { SharePrivateContentExpirationDateDisplay } from '~/components/SharePrivateContentModal/SharePrivateContentExpirationDateDisplay';

type SharePrivateClipLinkInfoProps = {
  shortIdInfo: ShortUrlResponse;
  clip: Pick<Clip, 'id' | 'assetVersionCount' | 'ext'>;
  trackLocation: AirActionTrackingLocation;
  goToSettings: () => void;
  boardContext: string | undefined;
  userPermissions: ScopedPermissions;
};

export const SharePrivateClipLinkInfo = memo(
  ({
    shortIdInfo,
    clip,
    trackLocation,
    goToSettings,
    boardContext,
    userPermissions,
  }: SharePrivateClipLinkInfoProps) => {
    const [selectedPage, setSelectedPage] = useState<number | undefined>(undefined);
    const { onShortUrlUpdate, isLoading } = useUpdateShareClipLink({
      clipId: clip.id,
      shortIdInfo,
    });

    const onToggleEnableLink = useCallback(
      (enabled: boolean) =>
        onShortUrlUpdate({
          disabled: !enabled,
        }),
      [onShortUrlUpdate],
    );

    const onDownloadToggle = useCallback<EnableSharedLinkToggleProps['onToggle']>(
      (enabled) =>
        onShortUrlUpdate({
          permissions: {
            asset: {
              canViewOriginal: enabled,
            },
          },
        }),
      [onShortUrlUpdate],
    );

    const urlSearchParams = useMemo(() => {
      if (!selectedPage) return undefined;

      const searchParams = new URLSearchParams();

      searchParams.set('page', selectedPage.toString());

      return searchParams;
    }, [selectedPage]);

    return (
      <div className="flex flex-col gap-3">
        <EnableSharedLinkToggle enabled={!shortIdInfo.disabled} isLoading={isLoading} onToggle={onToggleEnableLink} />
        {!shortIdInfo.disabled && (
          <>
            <EnableShareLinkDownloadToggle
              checked={shortIdInfo.permissions.asset.canViewOriginal}
              isLoading={isLoading}
              onToggle={onDownloadToggle}
              infoText="This asset or version can be downloaded or saved to other Air workspaces"
              disabled={!userPermissions.asset.canViewOriginal}
            />
            <EditableClipSharePrivateLink
              boardContext={boardContext}
              clip={clip}
              trackLocation={trackLocation}
              shortIdInfo={shortIdInfo}
              searchParams={urlSearchParams}
            />
            {!!shortIdInfo.expirationDate && (
              <SharePrivateContentExpirationDateDisplay
                expirationDate={shortIdInfo.expirationDate}
                goToAdvancedSettings={goToSettings}
              />
            )}
            {isPdfRenderedExtension(clip.ext) && <SharePrivateClipPDFPage onSelectPage={setSelectedPage} />}
          </>
        )}
      </div>
    );
  },
);

SharePrivateClipLinkInfo.displayName = 'SharePrivateClipLinkInfo';
